/* ==========================================================================
   SASH STORE — "THE STAGE"
   Products as panels floating in a void, scroll driving a camera through them.

   CSS 3D transforms, not WebGL: every panel stays a real <a> with real text,
   so it is clickable, crawlable and screen-reader readable, and the page
   carries no 3D-engine payload. Eligibility comes from window.SashGate and is
   re-checked on resize, so widening a window turns the stage on.

   PERFORMANCE NOTE: every geometry read is cached and refreshed only on
   resize. An earlier version read viewport.clientWidth/Height and
   getBoundingClientRect() inside the animation frame, which forced a layout
   on each of ten panels every frame and saturated the main thread.
   The render loop now only writes.
   ========================================================================== */
(function () {
  'use strict';

  var G = window.SashGate;
  if (!G) return;                       // gate script missing: stay flat

  var MIN_WIDTH = 680;
  function ok() { return G.stageOK(MIN_WIDTH); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* ======================================================================
     1. THE STAGE — homepage camera flight
     Returns true once it has taken over; false means "ask again on resize".
     ====================================================================== */
  function initStage() {
    var stage = document.querySelector('[data-sash-stage]');
    if (!stage) return true;
    if (stage.getAttribute('data-mode') === '3d') return true;
    if (!(G.motionOK() && !G.prefersFlat() && window.innerWidth >= 320)) { stage.setAttribute('data-mode', 'flat'); return false; }

    var scene = stage.querySelector('.sash-stage__scene');
    var viewport = stage.querySelector('.sash-stage__viewport');
    var panels = Array.prototype.slice.call(stage.querySelectorAll('.sash-panel'));
    if (!scene || !viewport || !panels.length) { stage.setAttribute('data-mode', 'flat'); return true; }

    stage.setAttribute('data-mode', '3d');

    // Panels are authored once inside the flat grid and MOVED here, never
    // duplicated: one set of nodes, one set of images, identical markup
    // whether or not the 3D runs.
    panels.forEach(function (p) { scene.appendChild(p); });
    scene.removeAttribute('aria-hidden');

    var mobile = !G.fine.matches || window.innerWidth < 680;
    var SPAN = mobile ? Math.max(2400, panels.length * 650) : 6200;      // depth of the corridor, px
    var NEAR = -120;      // past this a panel has gone by the camera
    var seats = [
      { x: -0.30, y: -0.16, r:  7 },
      { x:  0.31, y:  0.11, r: -8 },
      { x: -0.17, y:  0.25, r:  5 },
      { x:  0.21, y: -0.27, r: -6 },
      { x: -0.34, y:  0.04, r:  9 },
      { x:  0.14, y:  0.24, r: -4 }
    ];

    var layout = panels.map(function (p, i) {
      var s = mobile ? { x: i % 2 ? 0.09 : -0.09, y: i % 2 ? 0.07 : -0.07, r: i % 2 ? -6 : 6 } : seats[i % seats.length];
      var t = panels.length > 1 ? i / (panels.length - 1) : 0;
      return { el: p, z0: -(400 + SPAN * t), x: s.x, y: s.y, r: s.r, near: false, vis: true };
    });

    var cam = 0, targetCam = 0, raf = null, running = true;

    // --- cached geometry: written only by measure() ---
    var vw = 0, vh = 0, stageTop = 0, travel = 1;

    function measure() {
      if (mobile) stage.style.height = Math.max(400, panels.length * 65) + 'svh';
      vw = viewport.clientWidth;
      vh = viewport.clientHeight;
      stageTop = stage.getBoundingClientRect().top + window.scrollY;
      travel = Math.max(1, stage.offsetHeight - vh);
    }

    function readScroll() {
      return clamp((window.scrollY - stageTop) / travel, 0, 1);
    }

    // Write-only: no layout reads in here.
    function place() {
      for (var i = 0; i < layout.length; i++) {
        var L = layout[i], z = L.z0 + cam, op;

        if (mobile && z < -2200) op = 0;
        else if (mobile && z < -1600) op = (z + 2200) / 600;
        else if (z > NEAR) op = 0;
        else if (z > -420) op = (NEAR - z) / (NEAR + 420);
        else if (z < -SPAN * 0.92) op = clamp((z + SPAN + 600) / 600, 0, 1);
        else op = 1;

        var vis = op >= 0.01;
        if (vis !== L.vis) {
          L.vis = vis;
          L.el.style.visibility = vis ? 'visible' : 'hidden';
        }
        if (!vis) continue;

        L.el.style.opacity = op.toFixed(3);
        L.el.style.transform =
          'translate3d(' + (L.x * vw).toFixed(1) + 'px,' +
          (L.y * vh).toFixed(1) + 'px,' + z.toFixed(1) + 'px) ' +
          'rotateY(' + L.r + 'deg)';

        var near = !mobile && z > -900 && z < NEAR;
        if (near !== L.near) {
          L.near = near;
          L.el.classList.toggle('is-breaking', near);
        }
      }
    }

    function frame() {
      cam += (targetCam - cam) * 0.09;
      place();
      if (Math.abs(targetCam - cam) > 0.4) {
        raf = requestAnimationFrame(frame);
      } else {
        cam = targetCam; place(); raf = null;
      }
    }

    function kick() {
      targetCam = readScroll() * (SPAN + 700);
      if (!raf && running) raf = requestAnimationFrame(frame);
    }

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; kick(); });
    }, { passive: true });

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () { measure(); kick(); }, 180);
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) {
        running = e[0].isIntersecting;
        if (running) kick(); else if (raf) { cancelAnimationFrame(raf); raf = null; }
      }, { threshold: 0 }).observe(stage);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden && raf) { cancelAnimationFrame(raf); raf = null; }
      else if (!document.hidden) kick();
    });

    measure();
    cam = readScroll() * (SPAN + 700);
    targetCam = cam;
    place();
    stage.classList.add('is-live');

    if (!mobile) startDust(stage.querySelector('.sash-stage__dust'));
    wireFlatToggle(stage);
    return true;
  }

  /* ======================================================================
     2. DUST — the particle field behind the panels
     ====================================================================== */
  function startDust(canvas) {
    if (!canvas || canvas.dataset.sashDust) return;
    canvas.dataset.sashDust = '1';
    var ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    var w = 0, h = 0, bits = [], raf = null, on = true;

    function size() {
      var r = canvas.getBoundingClientRect();
      w = r.width; h = r.height;
      if (!w || !h) return;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var n = Math.min(Math.round((w * h) / 12000), 110);
      bits = [];
      for (var i = 0; i < n; i++) {
        bits.push({
          x: Math.random() * w, y: Math.random() * h,
          z: Math.random() * 0.9 + 0.1, r: Math.random() * 1.5 + 0.3,
          a: Math.random() * 0.55 + 0.1, tw: Math.random() * 6.28
        });
      }
    }

    function draw() {
      ctx.clearRect(0, 0, w, h);
      for (var i = 0; i < bits.length; i++) {
        var b = bits[i];
        b.y -= 0.16 * b.z;
        b.x += 0.05 * (b.z - 0.5);
        b.tw += 0.018;
        if (b.y < -8) { b.y = h + 8; b.x = Math.random() * w; }
        var a = b.a * (0.55 + 0.45 * Math.sin(b.tw));
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r * b.z, 0, 6.2832);
        ctx.fillStyle = 'rgba(206, 198, 255, ' + a.toFixed(3) + ')';
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    }

    function start() { if (!raf && on && !document.hidden) raf = requestAnimationFrame(draw); }
    function stop() { if (raf) { cancelAnimationFrame(raf); raf = null; } }

    size(); start();

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) {
        on = e[0].isIntersecting;
        on ? start() : stop();
      }, { threshold: 0 }).observe(canvas);
    }

    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t); t = setTimeout(size, 200);
    }, { passive: true });
  }

  /* ======================================================================
     3. THE CORRIDOR — collection and search pages in depth

     Depth is deliberately shallow: a deep push-back shrinks a card inside a
     grid cell that keeps its full height, which left large empty bands
     between rows and made a 45-product collection unreadable.
     ====================================================================== */
  function initCorridor() {
    var main = document.querySelector('main[data-template]');
    if (!main) return true;
    var tpl = main.getAttribute('data-template') || '';
    if (!/collection|search|list-collections/.test(tpl)) return true;

    var grid = document.querySelector('#product-grid, .product-grid, .collection .grid');
    if (!grid) return true;
    if (grid.getAttribute('data-sash-depth') === '3d') return true;

    injectViewToggle(grid);

    if (!ok()) { grid.setAttribute('data-sash-depth', 'flat'); return false; }
    grid.setAttribute('data-sash-depth', '3d');

    var items = Array.prototype.slice.call(grid.children);
    var steps = [-170, -50, -240, -90, -200, 0];
    items.forEach(function (li, i) {
      li.style.setProperty('--sash-z', steps[i % steps.length] + 'px');
      li.style.setProperty('--sash-ry', ((i % 2 ? 1 : -1) * 4) + 'deg');
    });

    // Offsets are measured once per resize, then the scroll pass is pure maths.
    var tops = [], heights = [], ticking = false;

    function measure() {
      tops = []; heights = [];
      for (var i = 0; i < items.length; i++) {
        var r = items[i].getBoundingClientRect();
        tops.push(r.top + window.scrollY);
        heights.push(r.height);
      }
    }

    function pass() {
      ticking = false;
      var vh = window.innerHeight, y = window.scrollY, half = vh / 2;
      for (var i = 0; i < items.length; i++) {
        var top = tops[i] - y;
        if (top + heights[i] < -200 || top > vh + 200) continue;
        var p = (half - (top + heights[i] / 2)) / half;
        items[i].style.setProperty('--sash-lift', (p * 16).toFixed(1) + 'px');
      }
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(pass); }
    }, { passive: true });

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt); rt = setTimeout(function () { measure(); pass(); }, 200);
    }, { passive: true });

    measure();
    pass();
    return true;
  }

  /* ======================================================================
     4. VIEW TOGGLE — an escape hatch out of the depth view
     ====================================================================== */
  function injectViewToggle(anchor) {
    if (document.querySelector('.sash-viewtoggle')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sash-viewtoggle';
    btn.setAttribute('aria-pressed', G.prefersFlat() ? 'true' : 'false');
    btn.textContent = G.prefersFlat() ? 'Depth view' : 'Flat view';
    btn.addEventListener('click', function () {
      G.setFlat(!G.prefersFlat());
      window.location.reload();
    });
    var host = anchor.parentNode;
    if (host) host.insertBefore(btn, anchor);
  }

  function wireFlatToggle(stage) {
    var btn = stage.querySelector('[data-sash-flat]');
    if (!btn || btn.dataset.sashWired) return;
    btn.dataset.sashWired = '1';
    btn.addEventListener('click', function () {
      G.setFlat(true);
      window.location.reload();
    });
  }

  /* ======================================================================
     5. BOOT — and keep asking until the viewport qualifies
     ====================================================================== */
  var done = { stage: false, corridor: false };

  function boot() {
    if (!done.stage) done.stage = initStage();
    if (!done.corridor) done.corridor = initCorridor();
  }

  function recheck() {
    if (done.stage && done.corridor) return;
    boot();
  }

  var rt;
  window.addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(recheck, 250);
  }, { passive: true });

  if (G.reduced.addEventListener) G.reduced.addEventListener('change', recheck);
  if (G.fine.addEventListener) G.fine.addEventListener('change', recheck);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    done.stage = false;
    done.corridor = false;
    boot();
  });
})();
